'use client'

import { useState, useCallback } from 'react'
import Link from 'next/link'
import { CaretLeftIcon } from '@phosphor-icons/react'
import { cn } from '@/lib/tailwind'
import { useUpdateProject } from './write-spec'
import { useProjectStats } from './select-project-stats'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import { TEXT_STYLES, FOCUS_STYLES } from '@/ui/design-tokens'
import type { Project, ProjectStatus } from '@/document'

interface ProjectSidebarProps {
  project: Project
}

const STATUS_LABELS: Record<ProjectStatus, string> = {
  active: 'Active',
  on_hold: 'On Hold',
  completed: 'Completed',
}

/**
 * Sidebar for project detail page.
 * Shows project name, editable description, status dropdown, and task progress.
 * Desktop-only, sticky positioning.
 */
export function ProjectSidebar({ project }: ProjectSidebarProps) {
  const updateProject = useUpdateProject()
  const { data: allStats } = useProjectStats()
  const stats = allStats[project.id] ?? {
    total: 0,
    completed: 0,
    percentage: 0,
    overdue: 0,
  }

  const [isEditingDescription, setIsEditingDescription] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState(project.description)

  const handleStatusChange = useCallback(
    (status: string) => {
      updateProject.mutate({
        id: project.id,
        data: { status: status as ProjectStatus },
      })
    },
    [updateProject, project.id],
  )

  const handleDescriptionSave = useCallback(() => {
    const trimmed = descriptionDraft.trim()
    if (trimmed !== project.description) {
      updateProject.mutate({
        id: project.id,
        data: { description: trimmed },
      })
    }
    setIsEditingDescription(false)
  }, [descriptionDraft, project.description, project.id, updateProject])

  const handleDescriptionKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleDescriptionSave()
      }
      if (e.key === 'Escape') {
        setDescriptionDraft(project.description)
        setIsEditingDescription(false)
      }
    },
    [handleDescriptionSave, project.description],
  )

  return (
    <aside aria-label="Project sidebar" className="px-3 py-4">
      {/* Back link */}
      <Link
        href="/app/projects"
        className={cn(
          'text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1 text-sm transition-colors',
          FOCUS_STYLES.ringCompact,
        )}
        aria-label="Back to projects"
      >
        <CaretLeftIcon className="h-3.5 w-3.5" weight="bold" />
        Projects
      </Link>

      {/* Project name */}
      <h2 className="mt-4 font-[family-name:var(--font-instrument-serif)] text-lg">
        {project.name}
      </h2>

      {/* Description — click to edit */}
      <div className="mt-2">
        {isEditingDescription ? (
          <textarea
            value={descriptionDraft}
            onChange={(e) => setDescriptionDraft(e.target.value)}
            onBlur={handleDescriptionSave}
            onKeyDown={handleDescriptionKeyDown}
            className={cn(
              'text-muted-foreground w-full resize-none rounded-sm bg-transparent p-0 text-sm transition-shadow',
              FOCUS_STYLES.ringCompact,
            )}
            rows={3}
            autoFocus
            aria-label="Edit project description"
          />
        ) : (
          <button
            type="button"
            onClick={() => {
              setDescriptionDraft(project.description)
              setIsEditingDescription(true)
            }}
            className={cn(
              'text-muted-foreground hover:text-foreground w-full cursor-pointer text-left text-sm transition-colors',
              FOCUS_STYLES.ringCompact,
            )}
            aria-label="Edit project description"
          >
            {project.description || 'Add a description...'}
          </button>
        )}
      </div>

      {/* Divider */}
      <div className="border-border my-4 border-t" />

      {/* Status */}
      <div className="space-y-2">
        <h3 className={TEXT_STYLES.sectionHeader}>Status</h3>
        <Select value={project.status} onValueChange={handleStatusChange}>
          <SelectTrigger
            size="sm"
            className="w-full"
            aria-label="Project status"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">{STATUS_LABELS.active}</SelectItem>
            <SelectItem value="on_hold">{STATUS_LABELS.on_hold}</SelectItem>
            <SelectItem value="completed">{STATUS_LABELS.completed}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Task progress */}
      <div className="mt-4 space-y-2">
        <h3 className={TEXT_STYLES.sectionHeader}>Progress</h3>
        {stats.total > 0 && (
          <div className="mt-2">
            <div className="bg-border/50 h-1.5 w-full overflow-hidden rounded-full">
              <div
                className="bg-accent h-full rounded-full transition-all duration-300 ease-out"
                style={{ width: `${stats.percentage}%` }}
              />
            </div>
            <p className="text-muted-foreground/60 mt-1.5 text-xs tabular-nums">
              {stats.completed}/{stats.total} tasks
            </p>
          </div>
        )}
        {stats.total === 0 && (
          <p className="text-muted-foreground text-sm tabular-nums">0 tasks</p>
        )}
        {stats.overdue > 0 && (
          <p className="text-muted-foreground mt-1 text-xs">
            {stats.overdue} {stats.overdue === 1 ? 'task needs' : 'tasks need'}{' '}
            attention
          </p>
        )}
      </div>
    </aside>
  )
}
